import { ApiService, ApiSyncStatus } from "@incendium/api";
import {
  Box,
  Button,
  Divider,
  List,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import { syncService } from "Apis";
import {
  StyledDrawerContainer,
} from "Components/UI/StyledDrawer";
import { useNotification, useSelectedProject } from "Hooks";
import { useExternalSyncs } from "Hooks/useExternalSyncs";
import { useSyncClients } from "Hooks/useSyncClients";
import produce from "immer";
import { Fragment, useCallback } from "react";
import { useConfirmationContext } from "Providers/ConfirmationProvider";
import SyncStyledListItem from "features/dataConnectors/components/SyncStyledListItem";
import ShopifyConnectButton from "features/dataConnectors/components/ShopifyConnectButton";
import ConnectButton from "features/dataConnectors/components/ConnectButton";
import { OAUTH_REDIRECT_URL } from "features/auth/constants";
import { ISyncConfigComponentProps } from "features/dataConnectors/types";

function Shopify({
  oauthClient,
  syncs,
  open,
  setOpen,
}: ISyncConfigComponentProps) {
  const { selectedProject } = useSelectedProject();
  const { syncClients, setSyncClients } = useSyncClients();
  const { externalSyncs, setExternalSyncs, refetch: refetchExternalSyncs } =
    useExternalSyncs();
  const { openConfirmation, closeConfirmation } = useConfirmationContext();
  const { showSuccessNotification, showErrorNotification } = useNotification();

  const removeStore = useCallback(
    async (id: number) => {
      openConfirmation({
        title: (
          <>
            <Typography variant="subtitle1">
              Are you sure you want to remove this Shopify Store?
            </Typography>
            <Typography variant="body2">
              Doing so will remove all data associated with this store.
            </Typography>
          </>
        ),
        callback: async () => {
          try {
            await syncService.syncServiceDeleteSync({
              projectId: selectedProject?.id as number,
              syncId: id,
            });

            setExternalSyncs(
              produce(externalSyncs, (draft) => {
                const idx = draft.findIndex((d) => d.id === id);
                if (idx >= 0) {
                  draft.splice(idx, 1);
                }
              })
            );

            showSuccessNotification(`Store removed.`);
          } catch (error) {
            showErrorNotification(`Failed to remove store.`);
          }

          closeConfirmation();
        },
      });
    },
    [
      openConfirmation,
      closeConfirmation,
      selectedProject,
      showSuccessNotification,
      showErrorNotification,
      externalSyncs,
      setExternalSyncs,
    ]
  );

  const onConnected = useCallback(async () => {
    // Linking happens server-side during auth when `linkAccountProjectId` is provided.
    // Refresh syncs so the drawer reflects the newly linked store.
    try {
      await refetchExternalSyncs();
      showSuccessNotification("Store connected successfully!");
    } catch {
      showErrorNotification("Failed to refresh store connection status.");
    }
  }, [refetchExternalSyncs, showSuccessNotification, showErrorNotification]);

  return (
    <Box sx={{ maxWidth: 600 }}>
      <StyledDrawerContainer>
        <Box>
          <Typography variant="subtitle1">Connected Shopify Stores</Typography>
          <Typography variant="body2" mb={2}>
            {(syncs || []).length > 0
              ? "Manage your connected Shopify stores below"
              : "Connect a Shopify store to get started"}
          </Typography>

          {(syncs || []).length > 0 && (
            <List>
              {(syncs || []).map((s) => (
                <Fragment key={s.id}>
                  <SyncStyledListItem
                    disableGutters
                    error={s.status === ApiSyncStatus.ERROR}
                    secondaryAction={
                      <Stack spacing={1}>
                        {s.status === ApiSyncStatus.ERROR && (
                          <ConnectButton
                            service={ApiService.SHOPIFY}
                            syncClients={syncClients}
                            setSyncClients={setSyncClients}
                            oauthClientId={s.oauthClientId}
                            onConnected={onConnected}
                            state={OAUTH_REDIRECT_URL}
                          />
                        )}
                        <Button
                          size="extraSmall"
                          variant="outlined"
                          onClick={() => removeStore(s.id as number)}
                        >
                          Remove Store
                        </Button>
                      </Stack>
                    }
                  >
                    <ListItemText
                      primary={`${s.accountName}`}
                      secondary={
                        s.status === ApiSyncStatus.ERROR ? (
                          <Typography variant="subtitle2" color="error">
                            This connection is no longer valid, please reconnect
                          </Typography>
                        ) : (
                          <Typography variant="body2">
                            {s.client?.user?.name}
                          </Typography>
                        )
                      }
                    />
                  </SyncStyledListItem>
                </Fragment>
              ))}
            </List>
          )}
        </Box>
      </StyledDrawerContainer>

      <Divider />

      <StyledDrawerContainer>
        <Box>
          <Typography variant="subtitle1" mb={1}>
            Add Another Store
          </Typography>
          <Typography variant="body2" mb={2}>
            Connect an additional Shopify store.
          </Typography>
          <ShopifyConnectButton
            service={ApiService.SHOPIFY}
            syncClients={syncClients}
            setSyncClients={setSyncClients}
            state={OAUTH_REDIRECT_URL}
            linkAccountProjectId={selectedProject?.id as number}
            onConnected={onConnected}
          />
        </Box>
      </StyledDrawerContainer>
    </Box>
  );
}

export default Shopify;
